"use client";

import "./styles.css";

import { yupResolver } from "@hookform/resolvers/yup";
import type React from "react";
import { Controller, useForm } from "react-hook-form";

import {
  type AddQuoteResult,
  InsuredParty,
  type PolicyHolder,
  ProductType,
  type TravelEntryPage,
  type TravelQuote,
  type WebShopPublicUser,
} from "@/api_client";
import { useEntry } from "@/hooks/useEntry";
import {
  type TravelEntryFormValues,
  TravelEntryValidationSchema,
} from "@/validators/products/travel/TravelEntryValidator";
import {
  ComposedRadioButton,
  ComposedRadioButtonValue,
} from "../../../shared/composedRadioButton/ComposedRadioButton";
import { ProductContainer } from "../../../shared/productContainer/ProductContainer";
import { SsnInput } from "../../../shared/ssnInput/SsnInput";

export type EntryProps = {
  externalUser?: WebShopPublicUser;
  externalPolicyHolder?: PolicyHolder | null;
  externalContent?: TravelEntryPage | null;
  externalIsLoading?: boolean;
  externalSubmitHandler?: (quote: TravelQuote) => Promise<AddQuoteResult>;
};

export const TravelEntryForm = ({
  externalUser,
  externalSubmitHandler,
  externalIsLoading,
  externalContent,
  externalPolicyHolder,
}: EntryProps): React.JSX.Element => {
  const {
    currentUser,
    currentPolicyHolder,
    content,
    isLoading,
    submitHandler,
  } = useEntry<TravelQuote, TravelEntryPage>({
    externalUser,
    externalSubmitHandler,
    externalIsLoading,
    externalContent,
    externalPolicyHolder,
    contentKey: "travelEntryPage",
  });

  const defaultValues: TravelQuote = {
    policyHolderSsn:
      currentPolicyHolder?.prefilledSsn ?? currentUser?.ssn ?? "",
    productType: ProductType.TRAVEL,
    coveredPerson: {
      insuredParty: InsuredParty.POLICY_HOLDER,
      familyMember: {
        ssn: "",
      },
    },
  };
  const { handleSubmit, control, formState, watch } =
    useForm<TravelEntryFormValues>({
      defaultValues,
      resolver: yupResolver(TravelEntryValidationSchema(content)),
      disabled: isLoading,
    });
  const insuredParty = watch("coveredPerson.insuredParty");
  const { errors } = formState;
  return (
    <ProductContainer>
      <ProductContainer.Entry>
        <ProductContainer.Form
          noValidate
          onSubmit={handleSubmit(submitHandler)}
        >
          <Controller
            control={control}
            name="policyHolderSsn"
            render={({ field }) => (
              <SsnInput
                disabled={
                  currentPolicyHolder?.ssnLocked ||
                  currentUser?.isAuthenticated ||
                  isLoading
                }
                id="policyHolderSsn"
                placeholder={
                  content?.entryPageCommon?.ssnQuestion?.placeholder?.value ??
                  undefined
                }
                popoverMessage={
                  content?.entryPageCommon?.ssnQuestion?.popover?.value ?? ""
                }
                data-testid="policy-holder-ssn-input"
                errorMessage={errors?.policyHolderSsn?.message}
                {...field}
              >
                {content?.entryPageCommon?.ssnQuestion?.label?.value}
              </SsnInput>
            )}
          />
          <Controller
            control={control}
            name="coveredPerson.insuredParty"
            render={({ field }) => (
              <ComposedRadioButton
                disabled={isLoading}
                id="insuredParty"
                label={content?.coveredPersonQuestion?.label?.value}
                errorMessage={errors?.coveredPerson?.insuredParty?.message}
                popoverMessage={content?.coveredPersonQuestion?.popover?.value}
                invalid={undefined}
                className={"if"}
                {...field}
                value={field.value?.toString()}
              >
                <ComposedRadioButtonValue
                  disabled={isLoading}
                  defaultChecked={true}
                  name="coveredPerson.insuredParty"
                  id="insuredParty.myself"
                  data-testid="insured-party-myself-radio"
                  label={content?.coveredPersonMyselfLabel?.value ?? ""}
                  value={InsuredParty.POLICY_HOLDER}
                />
                <ComposedRadioButtonValue
                  disabled={isLoading}
                  name="coveredPerson.insuredParty"
                  id="insuredParty.familyMember"
                  data-testid="insured-party-family-member-radio"
                  label={content?.coveredPersonSpouseLabel?.value ?? ""}
                  value={InsuredParty.FAMILY_MEMBER}
                />
              </ComposedRadioButton>
            )}
          />
          {insuredParty === InsuredParty.FAMILY_MEMBER && (
            <Controller
              control={control}
              name="coveredPerson.familyMember.ssn"
              render={({ field }) => (
                <SsnInput
                  id="ssn"
                  placeholder={
                    content?.spouseSsnQuestion?.placeholder?.value ?? undefined
                  }
                  data-testid="covered-person-ssn-input"
                  className="if"
                  errorMessage={
                    errors?.coveredPerson?.familyMember?.ssn?.message
                  }
                  {...field}
                >
                  {content?.spouseSsnQuestion?.label?.value ?? ""}
                </SsnInput>
              )}
            />
          )}
          <ProductContainer.CallToAction
            className="if full-width-mobile-button mt-8"
            loading={isLoading}
            disabled={isLoading}
            htmlType="submit"
            data-testid="travel-entry-submit-button"
          >
            {content?.entryPageCommon?.submitButtonText?.value ?? ""}
          </ProductContainer.CallToAction>
        </ProductContainer.Form>
      </ProductContainer.Entry>
    </ProductContainer>
  );
};
